// **YUP Imports
import * as yup from 'yup'

// **REGEX Imports
import {
  emailRegex,
  PHONE_NUMBER_REGEX,
  STRONG_PASSWORD_REGEX,
  SUBDOMAIN_VALIDATION_REGEX
} from './regex'

// **Constants Imports
import {
  APPLICATION_SUBTYPES,
  CHALLENGE_TESTCASE_TYPE,
  CHALLENGE_TYPE_FROM_ID,
  CHALLENGE_TYPE_ID,
  CHALLENGE_TYPES,
  REGISTRATION_FORM_COMPONENT_TYPES_LABEL_MAP,
  REGISTRATION_FORM_COMPONENT_TYPES_VALUE_MAP
} from './constant'

// =====================================================================================================
//                                       (o)SHARED VALIDATIONS
// =====================================================================================================
export const validateRequiredString = (label, requiredTrim = true) => {
  return requiredTrim
    ? yup.string().trim().required(`${label} is required`)
    : yup.string().required(`${label} is required`)
}

export const validateName = (label = 'Name') => {
  return yup
    .string()
    .max(100, `${label} cannot exceed 100 characters`)
    .required(`${label} is required`)
}

export const scoreValidation = (min = 0, max = 10) => {
  return yup
    .number()
    .integer('Score must be an integer value')
    .min(min, `Score number must be between ${min} and ${max}`)
    .max(max, `Score number must be between ${min} and ${max}`)
    .typeError(`Score number must be numeric value`)
    .required(`Score is required`)
}

// =====================================================================================================
//                                      (o)DURATION AND PENALTY RELATED
// =====================================================================================================

//#when duration is compulsory and required
export const requiredDurationValidation = (
  durationLimit = 2147483647,
  maxDurationMessage = 'Invalid value for duration'
) => {
  return yup
    .number()
    .typeError('Duration must be a number')
    .integer('Duration must be an integer value')
    .moreThan(0, 'Duration must be greater than zero')
    .max(durationLimit, maxDurationMessage)
    .required('Duration is required')
}

//#when duration is nullable
export const suggestedDurationValidations = yup
  .mixed()
  .test('is-integer', 'Duration must be an integer value', (value) => {
    return Number.isInteger(Number(value))
  })
  .test(
    'is-empty-or-positive-integer',
    'Duration must be greater than zero',
    (value) => {
      return value === '' || value === null || (Number(value) && value > 0)
    }
  )
  .test('is-below-max-duration', 'Invalid value for duration', (value) => {
    return (
      value === '' ||
      value === null ||
      (Number.isInteger(Number(value)) && Number(value) <= 2147483647)
    )
  })
  .notRequired()

// #penalty validations
export const penaltyValidations = yup
  .mixed()
  .test('is-integer', 'Penalty must be an integer value', (value) => {
    return Number.isInteger(Number(value))
  })
  .test(
    'is-empty-or-positive-integer',
    'Please enter a penalty value between 0 and 100',
    (value) => {
      return (
        value === '' ||
        (Number.isInteger(Number(value)) &&
          Number(value) >= 0 &&
          Number(value) <= 100)
      )
    }
  )
  .nullable()

// =====================================================================================================
//                                     (o)AUTH RELATED VALIDATIONS
// =====================================================================================================

// # email
export const emailValidation = yup
  .string()
  .trim()
  .required('Email is required')
  .matches(emailRegex, 'Enter a valid email address')

// # password
export const passwordValidation = (needRegexMatching = false) => {
  return needRegexMatching
    ? yup
        .string()
        .required('Password is required')
        .matches(
          STRONG_PASSWORD_REGEX,
          'Password must be at least 8 characters long.'
        )
    : yup.string().required('Password is required')
}

// # confirm password
export const confirmPasswordValidation = yup
  .string()
  .oneOf(
    [yup.ref('password'), null],
    'Confirm password must match with password'
  )
  .required('Confirm Password is required')

// # phone number
export const phoneNumberValidation = yup
  .string()
  .max(20, 'Phone number must not exceed 20 characters')
  .matches(PHONE_NUMBER_REGEX, 'Enter a valid phone number')

export const changePasswordValidations = (hasPassword) =>
  yup.object().shape({
    previousPassword: hasPassword
      ? validateRequiredString('Current password')
      : yup.string().notRequired(),
    newPassword: yup
      .string()
      .required('New password is required')
      .matches(
        STRONG_PASSWORD_REGEX,
        'Password must be at least 8 characters long.'
      )
      .test(
        'not-same-as-previous',
        'Previous and new password cannot be the same',
        function (value) {
          const { previousPassword } = this.parent
          if (!value || !previousPassword) return true
          return value !== previousPassword
        }
      ),
    confirmPassword: yup
      .string()
      .oneOf(
        [yup.ref('newPassword'), null],
        'Confirm password must match with new password'
      )
      .required('Confirm Password is required')
  })

// =====================================================================================================
//                                  (o) VERIFY INVITED RELATED VALIDATIONS
// =====================================================================================================

// # name
export const verifyInvitedNameValidation = (candidatePath) => {
  return yup
    .string()
    .max(100, 'Name cannot exceeds 100 characters')
    .when([], {
      is: () => !candidatePath,
      then: () => yup.string().required('Full name is required'),
      otherwise: () => yup.string().notRequired()
    })
}

// # phoneNumber
export const verifyInvitedPhoneNumberValidation = (candidatePath) => {
  return yup
    .string()
    .max(20, 'Enter a valid phone number')
    .matches(PHONE_NUMBER_REGEX, 'Enter a valid phone number')
    .when([], {
      is: () => !candidatePath,
      then: () =>
        yup
          .string()
          .max(20, 'Enter a valid phone number')
          .matches(PHONE_NUMBER_REGEX, 'Enter a valid phone number')
          .required('Phone number is required'),
      otherwise: () => yup.string().notRequired()
    })
}

export const candidateDetailsValidations = yup.object().shape({
  name: validateRequiredString('Full name'),
  email: yup
    .string()
    .email('Please enter a valid email')
    .required('Email is required'),
  phoneNumber: yup
    .string()
    .max(20, 'Enter a valid phone number')
    .matches(PHONE_NUMBER_REGEX, 'Enter a valid phone number')
    .notRequired()
})

// =====================================================================================================
//                                     (o)CHALLENGE RELATED VALIDATIONS
// =====================================================================================================

// # Reusable validations
export const sharedLanguageDetailsValidation = (
  challengeType,
  challengeTestcaseType,
  challengeSubType
) => {
  return {
    language: validateRequiredString('Language'),
    languageRuntime:
      challengeSubType === APPLICATION_SUBTYPES.WEB_BASIC
        ? yup.string().notRequired()
        : validateRequiredString('Language Runtime'),
    projectMetadata: yup.object().shape({
      mainFile:
        challengeType === CHALLENGE_TYPES?.PROGRAMMING &&
        challengeTestcaseType !== CHALLENGE_TESTCASE_TYPE?.UNIT_TEST
          ? validateRequiredString('Main file')
          : yup.string(),
      testLibrary:
        challengeTestcaseType === CHALLENGE_TESTCASE_TYPE?.UNIT_TEST
          ? validateRequiredString('Test library')
          : yup.string()
    })
  }
}

// #code-tab validations
export const codeTabValidations = (challengeType, challengeTestcaseType) => {
  return sharedLanguageDetailsValidation(challengeType, challengeTestcaseType)
}

// # create challenge step-3 validation
export const languageDetailsValidation = (
  type,
  testcaseType,
  challengeSubType
) => {
  return yup
    .array()
    .of(
      yup.object().shape({
        ...sharedLanguageDetailsValidation(type, testcaseType, challengeSubType)
      })
    )
    .required('Language array is required')
    .min(1, 'At least one language is required')
}

// # create challenge step-2 validations
export const chooseLanguagesValidation = (
  applicationOrNotebookChallenge,
  multiSelectTechnology = false
) => {
  return applicationOrNotebookChallenge
    ? yup.string().required('Technology is required')
    : yup
        .array()
        .of(yup.string())
        .min(
          1,
          `At least one ${
            multiSelectTechnology ? 'technology' : 'language'
          } is required`
        )
        .required(
          `${multiSelectTechnology ? 'Technologies' : 'Languages'} are required`
        )
}

// # create challenge step-1 validations
export const basicStageValidations = (challengeType) => {
  return {
    name: validateRequiredString('Challenge name'),
    challengeSubType:
      challengeType === CHALLENGE_TYPES.APPLICATION
        ? validateRequiredString('Type')
        : yup.string().notRequired(),
    description: validateRequiredString('Challenge description'),
    testcaseType:
      challengeType === CHALLENGE_TYPES?.NOTEBOOK
        ? yup.string().notRequired()
        : validateRequiredString('Evaluation type'),
    duration: suggestedDurationValidations,
    databaseId:
      challengeType === CHALLENGE_TYPES?.DBT
        ? validateRequiredString('Database')
        : yup.string().notRequired(),
    difficulty: validateRequiredString('Difficulty')
  }
}

// # challenge edit validation (general page)
export const challengeEditValidations = (challengeType) => {
  return {
    name: validateRequiredString('Challenge name'),
    description: validateRequiredString('Challenge description'),
    challengeType: validateRequiredString('Challenge type'),
    testcaseType: validateRequiredString('Evaluation type'),
    duration: suggestedDurationValidations,
    difficulty: validateRequiredString('Difficulty'),
    ...(challengeType === 'DBT'
      ? { databaseId: validateRequiredString('Database') }
      : {})
  }
}

// =====================================================================================================
//                                 (o) MCQ RELATED VALIDATIONS
// =====================================================================================================
// # resusable
export const MCQTagsValidations = yup.array().of(yup.string())
export const MCQDifficultyValidations = yup
  .string()
  .required('Please select the difficulty for question')
export const MCQmarksValidations = yup
  .number('Marks must be number')
  .integer('Please enter valid integer value')
  .typeError('Please add integer value for marks')
  .min(1, 'You cannot assign less than 1 mark to a question')
  .max(100, 'You cannot assign more than 100 mark to a question')
  .required('Please enter mark for question')

// #update-add modal validations

export const difficultyValueSchema = yup
  .number()
  .typeError('Must be a number')
  .integer('Must be an integer')
  .max(100, 'Questions cannot exceed 100')
  .required('This field is required')

export const addUpdateMCQValidations = {
  tags: MCQTagsValidations,
  topicIds: yup
    .array()
    .of(yup.number())
    .min(1, 'Please select at least one skill.'),
  includeLibrary: yup
    .array()
    .of(yup.number())
    .min(1, 'Please select at least one library to include questions'),
  questionsSet: yup
    .object({
      EASY: difficultyValueSchema,
      MEDIUM: difficultyValueSchema,
      HARD: difficultyValueSchema
    })
    .test(
      'at-least-one-positive',
      'At least one difficulty must have more than 0 questions',
      (value) => {
        if (!value) return false
        return (
          Number(value.EASY) > 0 ||
          Number(value.MEDIUM) > 0 ||
          Number(value.HARD) > 0
        )
      }
    )
}

// #create-mcq-challenge step-1 validations
export const MCQChallengeStep1Validations = {
  name: validateRequiredString('Challenge name'),
  description: validateRequiredString('Challenge description'),
  penalty: penaltyValidations,
  duration: suggestedDurationValidations,
  difficulty: validateRequiredString('Difficulty')
}

// #edit mcq challenge validations
export const MCQChallengeEditValidations = {
  name: validateRequiredString('Challenge name'),
  challengeDescription: validateRequiredString('Challenge description'),
  challengeType: validateRequiredString('Challenge type'),
  duration: suggestedDurationValidations,
  penalty: penaltyValidations,
  difficulty: validateRequiredString('Difficulty')
}

// #create mcq challenge step-1
export const questionDetailsStep1Validations = {
  contentType: yup.string().required('Select type of question'),

  topicId: yup.number().required('Question Skill is required'),
  content: yup
    .string()
    .required('Please enter the question')
    .test('options-length', 'Please fill at least 2 options', function (value) {
      return this.parent.options && this.parent.options.length >= 2
    }),
  options: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string(),
        content: yup.string().required('Please fill the option details'),
        isCorrect: yup.boolean()
      })
    )
    .min(2, 'At least 2 options are required')
}

// #create mcq challenge step-2
export const questionDetailsStep2Validations = {
  mark: MCQmarksValidations,
  difficulty: MCQDifficultyValidations,
  tags: MCQTagsValidations
}

// =====================================================================================================
//                                  (o)SQL RELATED VALIDATIONS
// =====================================================================================================

// # create database modal
export const createDatabaseStep1Validations = {
  name: validateRequiredString('Challenge name'),
  description: validateRequiredString('Challenge description'),
  duration: suggestedDurationValidations
}

// # edit database
export const SQLGeneralValidations = {
  name: validateRequiredString('Challenge name'),
  description: validateRequiredString('Challenge description'),
  databaseId: validateRequiredString('Database'),
  evaluateScript: validateRequiredString('Evaluate script'),
  duration: suggestedDurationValidations,
  difficulty: validateRequiredString('Difficulty')
}

// =====================================================================================================
//                                 (o) AI INTERVIEW RELATED VALIDATIONS
// =====================================================================================================

export const topicGroupValidation = yup.object({
  topicGroup: yup.string().required('Topic group name is required'),
  weightage: yup
    .number()
    .typeError('Weightage must be a number')
    .min(1, 'Weightage must be at least 1')
    .max(10, 'Weightage cannot exceed 10')
    .required('Weightage is required'),
  approxDuration: yup
    .number()
    .typeError('Duration must be a number')
    .min(1, 'Duration must be at least 1 minute')
    .required('Duration is required'),
  topics: yup
    .array()
    .of(
      yup.object({
        topic: yup.string().required('Topic name is required'),
        subTopics: yup
          .array()
          .of(yup.string().required('Sub-topic cannot be empty'))
          .min(1, 'At least one sub-topic is required')
      })
    )
    .min(1, 'At least one topic is required')
})

// =====================================================================================================
//                                      (o)CONTEST VALIDATIONS
// =====================================================================================================

// #Resuseable validations
export const dynamicChallengeConfSchema = yup
  .array()
  .when(['challengeTypeId', 'dynamic'], {
    is: (challengeTypeId, dynamic) => {
      const id = parseInt(challengeTypeId)
      return (
        CHALLENGE_TYPE_FROM_ID?.[id]?.value === CHALLENGE_TYPES.MCQ &&
        dynamic === true
      )
    },
    then: () =>
      yup
        .array()
        .of(
          yup.object().shape({
            includeLibrary: yup
              .array()
              .of(yup.number())
              .min(1, 'Please select at least one library option.')
              .required(
                'Please select at least one library to include questions.'
              ),
            topicIds: yup
              .array()
              .of(yup.number())
              .min(1, 'Please select at least one skill.')
              .required('Skills are required.'),
            tags: yup.array().of(yup.string()),
            questionsSet: yup
              .array()
              .of(
                yup.object().shape({
                  difficulty: validateRequiredString('Difficulty'),
                  questionSize: yup
                    .number()
                    .typeError('Questions must be a number')
                    .integer('Questions must be an integer')
                    .required('Questions is required')
                })
              )
              .min(1, 'At least one difficulty is required')
              .required('Questions set is required')
          })
        )
        .min(1, 'Please add at least one question set'),
    otherwise: () => yup.array()
  })

const sharedContestValidationSchema = (assessment, fromEditPage) => {
  const baseShape = {
    name: yup
      .string()
      .required(`${assessment ? 'Assessment' : 'Contest'} name is required`)
      .max(
        100,
        `${
          assessment ? 'Assessment' : 'Contest'
        } name must be at most 100 characters long`
      ),

    ...(fromEditPage
      ? {
          description: validateRequiredString('Instructions')
        }
      : {}),
    contestUri: yup
      .string()
      .required(`${assessment ? 'Assessment' : 'Contest'} URL is required`),
    dynamicCandidateRegistration: yup.bool(),
    formId: yup
      .number('Invalid form')
      .typeError('Invalid form')
      .when('dynamicCandidateRegistration', {
        is: true,
        then: (schema) => schema.required('Application form is required'),
        otherwise: (schema) => schema.notRequired()
      }),
    closeRegistrationTimestamp: yup
      .mixed()
      .when('dynamicCandidateRegistration', {
        is: true,
        then: (schema) =>
          assessment
            ? schema.nullable().notRequired()
            : schema
                .test(
                  'close-application-validation',
                  'Submission deadline is required',
                  (value) => value != null && value !== ''
                )
                .test(
                  'close-application-valid-date',
                  'Enter valid date and time',
                  (value) => {
                    if (!value) return true
                    return !isNaN(new Date(value).getTime())
                  }
                ),
        otherwise: (schema) => schema.notRequired()
      }),
    ...(!assessment
      ? {
          startTime: yup.string().required('Contest start time is required')
        }
      : {})
  }

  return yup.object().shape(baseShape)
}

const startWindowDurationValidation = (isAssessment = false) => {
  return yup.number().when(['challengeTypeId', 'deadlineToStartStage'], {
    is: (challengeTypeId, deadlineToStartStage) => {
      const challengeId = parseInt(challengeTypeId)

      if (
        challengeId === CHALLENGE_TYPE_ID.PLACEMENT_TALK ||
        challengeId === CHALLENGE_TYPE_ID.VIDEO_RESPONSE ||
        challengeId === CHALLENGE_TYPE_ID.INTERVIEW
      ) {
        return false
      }

      if (deadlineToStartStage) {
        if (isAssessment) {
          return true
        }

        if (challengeId === CHALLENGE_TYPE_ID.AI_SCREENING && !isAssessment) {
          return true
        }
      }

      return false
    },
    then: () =>
      yup
        .number()
        .transform((_, originalValue) => {
          if (originalValue === '' || originalValue == null) {
            return null
          }
          return isNaN(Number(originalValue)) ? NaN : Number(originalValue)
        })
        .required('Deadline is required when enabled.')
        .typeError('Window time limit must be a valid number')
        .test(
          'is-positive',
          'Deadline must be greater than 0.',
          (value) => value == null || value > 0
        )
        .max(2147483647, 'Invalid value'),
    otherwise: () => yup.number().nullable().notRequired()
  })
}

// #Reusable validation
export const challengesWithWeightValidations = yup
  .array()
  .of(
    yup.object().shape({
      id: yup.string(),
      weight: yup
        .number()
        .typeError('Weight must be a number')
        .moreThan(0, 'Score must be greater than zero')
        .required('Score is required')
    })
  )
  .min(1, 'At least one challenge is required')

export const contestChallengeValidations = yup.array().when('challengeTypeId', {
  is: (value) => {
    const integerValue = value ? parseInt(value) : null

    return (
      integerValue !== CHALLENGE_TYPE_ID.MCQ &&
      integerValue !== CHALLENGE_TYPE_ID.INTERVIEW &&
      integerValue !== CHALLENGE_TYPE_ID.PLACEMENT_TALK &&
      integerValue !== CHALLENGE_TYPE_ID.AI_SCREENING &&
      integerValue !== CHALLENGE_TYPE_ID.VIDEO_RESPONSE
    )
  },
  then: () => challengesWithWeightValidations,
  otherwise: () => yup.array()
})

export const contestChallengeIdValidations = yup
  .string()
  .when('challengeTypeId', {
    is: CHALLENGE_TYPE_ID.MCQ.toString(), // for mcq
    then: () => validateRequiredString('Challenge'),
    otherwise: () => yup.string()
  })

// # create contest step-1
export const createContestStep1Validations = (assessment) =>
  sharedContestValidationSchema(assessment)

// # create contest step-2
// validation for stage object
export const setupStagesSchema = (isAssessment = false) => {
  return yup.object().shape({
    name: validateRequiredString('Stage name'),
    challengeTypeId: validateRequiredString('Stage type'),
    challengeId: yup.string().when('dynamic', {
      is: (value) => value,
      then: () => {
        return yup.string()
      },
      otherwise: () => {
        return contestChallengeIdValidations
      }
    }),
    challenges: yup.string().when('dynamic', {
      is: (value) => value,
      then: () => {
        return yup.array()
      },
      otherwise: () => {
        return contestChallengeValidations
      }
    }),
    challengeSubType: yup.string().when(['challengeTypeId'], {
      is: (value) => parseInt(value) === CHALLENGE_TYPE_ID.APPLICATION,
      then: () => {
        return validateRequiredString('Type')
      },
      otherwise: () => {
        return yup.string().notRequired()
      }
    }),
    deadlineDuration: yup.number().when(['challengeTypeId'], {
      is: (value) => parseInt(value) === CHALLENGE_TYPE_ID.VIDEO_RESPONSE,
      then: () => {
        return yup
          .number()
          .nullable()
          .integer('Time limit must be an integer value')
          .min(1, `Time limit must be greater then 0`)
          .max(2147483647, 'Invalid value')
          .typeError(`Time limit number must be numeric value`)
          .required(`Time limit is required`)
      },
      otherwise: () => {
        return yup.number().nullable().notRequired()
      }
    }),
    startWindowDuration: startWindowDurationValidation(isAssessment),
    startWindowDurationUnit: yup.string().notRequired(),
    deadlineUnit: yup.string().notRequired(),
    strictSubmission: yup.bool(),
    duration: yup.number().when('challengeTypeId', ([value]) => {
      if (value === CHALLENGE_TYPE_ID.AI_SCREENING.toString()) {
        return requiredDurationValidation(
          60,
          'Maximum duration should not exceed 60 minutes. Please adjust the approximate duration for groups.'
        )
      }
      if (value !== CHALLENGE_TYPE_ID.VIDEO_RESPONSE.toString()) {
        return requiredDurationValidation()
      }
      return yup.number().notRequired()
    }),
    jobSummary: yup.string().when('challengeTypeId', {
      is: CHALLENGE_TYPE_ID.AI_SCREENING.toString(), // for AI Interview
      then: () => {
        return yup.string().required('Job summary is required')
      },
      otherwise: () => {
        return yup.string()
      }
    }),
    evaluationSkills: yup.mixed().when('challengeTypeId', {
      is: CHALLENGE_TYPE_ID.AI_SCREENING.toString(),
      then: () =>
        yup
          .array()
          .of(topicGroupValidation)
          .min(1, 'At least one group is required')
          .required('At least one group is required'),
      otherwise: () => yup.array().of(yup.mixed().nullable())
    }),

    criteria: yup.mixed().when('challengeTypeId', (challengeTypeId, schema) => {
      if (
        parseInt(challengeTypeId) === CHALLENGE_TYPE_ID.MCQ ||
        parseInt(challengeTypeId) === CHALLENGE_TYPE_ID.PROGRAMMING ||
        parseInt(challengeTypeId) === CHALLENGE_TYPE_ID.SQL
      ) {
        return yup
          .number('Invalid progression score')
          .transform((value, originalValue) =>
            originalValue === '' || originalValue === null
              ? null
              : Number(originalValue)
          )
          .nullable()
          .min(0, 'Progression Score cannot be less than 0%')
          .max(100, 'Progression Score cannot exceed 100%')
          .typeError('Progression Score must be a valid number')
      } else {
        return schema.notRequired()
      }
    }),
    overrideLanguages: yup
      .array()
      .of(yup.string())
      .test('min-languages', function (value) {
        const { challengeTypeId } = this.parent

        if (
          parseInt(challengeTypeId) === CHALLENGE_TYPE_ID.DBT ||
          parseInt(challengeTypeId) === CHALLENGE_TYPE_ID.APPLICATION ||
          parseInt(challengeTypeId) === CHALLENGE_TYPE_ID.NOTEBOOK ||
          parseInt(challengeTypeId) === CHALLENGE_TYPE_ID.PROGRAMMING
        ) {
          return Array.isArray(value) && value.length > 0
            ? true
            : this.createError({
                message: `At least one ${
                  parseInt(challengeTypeId) === CHALLENGE_TYPE_ID.PROGRAMMING
                    ? 'language'
                    : 'technology'
                } is required`
              })
        } else {
          return true // No validation error
        }
      }),
    penalty: yup.string().when(['challengeTypeId', 'dynamic'], {
      is: (value, dynamic) =>
        parseInt(value) === CHALLENGE_TYPE_ID.MCQ && dynamic,
      then: () => {
        return penaltyValidations
      },
      otherwise: () => {
        return yup.string().notRequired()
      }
    }),
    dynamicChallengeConf: dynamicChallengeConfSchema,
    dynamic: yup.boolean(),
    videoQuestions: yup.array().when(['challengeTypeId'], {
      is: (value) => parseInt(value) === CHALLENGE_TYPE_ID.VIDEO_RESPONSE,
      then: () => {
        return yup.array().min(1, 'At least one question is required')
      },
      otherwise: () => {
        return yup.array().notRequired()
      }
    }),
    disableQuestionRevisit: yup.boolean()
  })
}

// # create contest step-4 candidates
export const createContestCandidatesValidations = (assessment) => {
  return yup.object().shape({
    emailInputs: yup
      .string()
      .trim()
      .test(
        'is-valid-emails',
        'One or more email addresses are invalid',
        function (value) {
          if (!value) return true // optional field
          const emails = value.split(',').map((e) => e.trim())
          const invalid = emails.filter((email) => !emailRegex.test(email))
          return invalid.length === 0
        }
      ),
    candidateEmails: yup.array().of(yup.string().email('Invalid email')),
    dynamicCandidateRegistration: yup.bool(),
    formId: yup
      .number('Invalid form')
      .typeError('Invalid form')
      .when('dynamicCandidateRegistration', {
        is: true,
        then: (schema) => schema.required('Application form is required'),
        otherwise: (schema) => schema.notRequired()
      }),
    closeRegistrationTimestamp: yup
      .mixed()
      .when('dynamicCandidateRegistration', {
        is: true,
        then: (schema) =>
          assessment
            ? schema.nullable().notRequired()
            : schema
                .test(
                  'close-application-validation',
                  'Submission deadline is required',
                  (value) => value != null && value !== ''
                )
                .test(
                  'close-application-valid-date',
                  'Enter valid date and time',
                  (value) => {
                    if (!value) return true
                    return !isNaN(new Date(value).getTime())
                  }
                ),
        otherwise: (schema) => schema.notRequired()
      }),
    ...(assessment && {
      isEnableNow: yup.boolean(),
      assessmentStartTime: yup.mixed().when('isEnableNow', {
        is: false,
        then: (schema) =>
          schema
            .test(
              'startTime-validation',
              'Assessment start time is required',
              (value) => value != null && value !== ''
            )
            .test(
              'startTime-valid-date',
              'Enter valid date and time',
              (value) => {
                if (!value) return true // Skip if empty; first test handles required
                return !isNaN(new Date(value).getTime())
              }
            ),
        otherwise: (schema) => schema.notRequired()
      })
    })
  })
}

// # create contest step 4 email template validations
export const emailTemplateValidations = yup.object().shape({
  subject: validateRequiredString('Subject'),
  title: validateRequiredString('Title'),
  emailBody: validateRequiredString('Email Body')
})

// validation for template's stage object
export const templateSchema = yup.object().shape({
  name: validateRequiredString('Stage Name'),
  challengeTypeId: yup.string().notRequired(),
  selectedChallengeType: yup.string().notRequired(),

  // validation for dynamic mcq
  dynamic: yup.boolean(),
  dynamicChallengeConf: dynamicChallengeConfSchema,
  overrideLanguages: yup
    .mixed()
    .when(
      ['selectedChallengeType', 'challengeTypeId'],
      ([selectedChallengeType, challengeTypeId], schema) => {
        const id = parseInt(challengeTypeId)
        const showValidation =
          id !== CHALLENGE_TYPE_ID.SQL &&
          id !== CHALLENGE_TYPE_ID.PLACEMENT_TALK &&
          id !== CHALLENGE_TYPE_ID.INTERVIEW &&
          id !== CHALLENGE_TYPE_ID.MCQ &&
          id !== CHALLENGE_TYPE_ID.AI_SCREENING &&
          id !== CHALLENGE_TYPE_ID.VIDEO_RESPONSE

        const message = `At least one ${
          id === CHALLENGE_TYPE_ID.PROGRAMMING ? 'language' : 'technology'
        } is required`

        return showValidation
          ? yup.array().of(yup.string()).min(1, message)
          : yup.array().of(yup.string())
      }
    )
})

export const jobOpeningPromptValidation = yup.object().shape({
  prompt: validateRequiredString('Prompt')
    .min(150, 'Prompt must be at least 150 characters long')
    .max(2000, 'Prompt must be at most 2000 characters long')
})

// # constest edit validations
export const contestGeneralValidations = (assessment, fromEditPage = false) =>
  sharedContestValidationSchema(assessment, fromEditPage)

// =====================================================================================================
//                                     (o)CONFIG RELATED VALIDATIONS
// =====================================================================================================
export const subDomainValidation = yup
  .string()
  .nullable() // Allow null values
  .max(63, 'Subdomain label must be no longer than 63 characters')
  .test(
    'no-consecutive-hyphens',
    'Subdomain cannot have consecutive hyphens',
    (value) => !value || !/--/.test(value)
  )
  .matches(
    SUBDOMAIN_VALIDATION_REGEX, // Regex for valid subdomain
    {
      message:
        'Subdomain label must start and end with a letter or number, and can contain hyphens in between',
      excludeEmptyString: true
    }
  )

export const configurationsValidations = {
  subDomain: subDomainValidation,
  timezone: validateRequiredString('Timezone'),
  primaryColor: validateRequiredString('Primary Color'),
  customContent: yup.array().of(
    yup.object().shape({
      userType: yup.string(),
      heading: yup
        .string()
        .nullable()
        .max(60, 'Heading must be no longer than 60 characters'),

      subHeading: yup
        .string()
        .nullable()
        .max(200, 'Subheading must be no longer than 200 characters')
    })
  )
}

// =====================================================================================================
//                                     (o)USERS PAGE VALIDATIONS
// =====================================================================================================
export const inviteUserSchema = yup.object().shape({
  name: validateName(),
  email: emailValidation,
  workspaceRoles: yup.array().of(
    yup.object().shape({
      workspaceId: yup.string().required(),
      role: yup.string(),
      name: yup.string()
    })
  )
})

export const approveUserSchema = (isDeveloper = false) => {
  return yup.object().shape({
    name: validateName(),
    email: emailValidation,
    tenantId: isDeveloper
      ? yup.string().required('Tenant selection is required.')
      : yup.string().notRequired(),
    workspaceRoles: yup.array().of(
      yup.object().shape({
        workspaceId: yup.string().required(),
        role: yup.string(),
        name: yup.string()
      })
    )
  })
}

// =====================================================================================================
//                                      (o)REGISTRATION FROM VALIDATIONS
// =====================================================================================================

export const fieldValidationSchema = yup.object().shape({
  name: validateRequiredString('Name'),
  type: yup
    .string()
    .oneOf(
      Object.keys(REGISTRATION_FORM_COMPONENT_TYPES_LABEL_MAP),
      'Invalid type'
    )
    .required('Type is required'),
  required: yup.boolean(),
  options: yup
    .array()
    .of(yup.string())
    .when('type', {
      is: (val) =>
        val === REGISTRATION_FORM_COMPONENT_TYPES_VALUE_MAP.Dropdown ||
        val ===
          REGISTRATION_FORM_COMPONENT_TYPES_VALUE_MAP['Multi Select Dropdown'],
      then: (schema) =>
        schema
          .min(1, 'Options are required for dropdown types')
          .required('Options are required'),
      otherwise: (schema) => schema.notRequired()
    }),
  formats: yup
    .array()
    .of(yup.string())
    .when('type', {
      is: (val) => val === 'file',
      then: (schema) =>
        schema
          .min(1, 'Formats are required for file type')
          .required('Formats are required'),
      otherwise: (schema) => schema.notRequired()
    }),
  criteria: yup.object({
    operator: yup.string().notRequired(),
    value: yup
      .number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value
      )
      .when('operator', {
        is: (val) => !!val,
        then: (schema) =>
          schema.required(
            'Criteria value is required when operator is selected'
          ),
        otherwise: (schema) => schema.notRequired()
      })
  })
})

export const constestFormValidation = yup.object().shape({
  name: validateRequiredString('Name'),
  instructions: validateRequiredString('Instructions'),
  disqualifyPreviouslyEnrolledCandidates: yup.boolean().required(),
  timeDuration: yup
    .object()
    .shape({
      durationValue: yup
        .number()
        .typeError('Count must be a number')
        .nullable(),
      durationUnit: yup.string().nullable()
    })
    .test('validate-timeDuration-fields', null, function (value) {
      const { disqualifyPreviouslyEnrolledCandidates } = this.parent
      if (disqualifyPreviouslyEnrolledCandidates) {
        const errors = []
        if (
          !(typeof value?.durationValue === 'number' && value.durationValue > 0)
        ) {
          errors.push(
            this.createError({
              path: 'timeDuration.durationValue',
              message: 'Count must be greater than 0'
            })
          )
        }
        if (!value?.durationUnit || value.durationUnit.trim() === '') {
          errors.push(
            this.createError({
              path: 'timeDuration.durationUnit',
              message: 'Time period is required'
            })
          )
        }
        if (errors.length > 0) throw new yup.ValidationError(errors)
      }
      return true
    }),
  fields: yup.array().of(fieldValidationSchema)
})

export const videoResponseQuestionSchema = yup.object().shape({
  title: validateRequiredString('Question title'),
  describe: yup.string().notRequired(),
  allowRetake: yup.boolean().notRequired(),
  videoPath: yup.string().notRequired(),
  isMandatory: yup.boolean().notRequired(),
  marks: yup
    .number()
    .nullable()
    .integer('Marks must be an integer value')
    .min(1, `Marks must be between 1 and 10`)
    .max(10, `Marks must be between 1 and 10`)
    .typeError(`Marks number must be numeric value`)
    .required(`Marks is required`),
  retakeCount: yup
    .number()
    .nullable()
    .when('allowRetake', {
      is: true,
      then: () =>
        yup
          .number()
          .nullable()
          .integer('Retake count must be an integer value')
          .min(1, `Retake count must be between 1 and 5`)
          .max(5, `Retake count must be between 1 and 5`)
          .typeError(`Retake count must be numeric value`)
          .required(`Retake count is required`),
      otherwise: (schema) => schema.notRequired()
    }),
  isFixedTime: yup.boolean().notRequired(),
  answerTime: yup
    .number()
    .nullable()
    .when('isFixedTime', {
      is: true,
      then: () =>
        yup
          .number()
          .nullable()
          .integer('Answer time must be an integer value')
          .min(1, `Answer time must be between 1 and 60`)
          .max(60, `Answer time must be between 1 and 60`)
          .typeError(`Answer time must be numeric value`)
          .required(`Answer time is required`),
      otherwise: (schema) => schema.notRequired()
    }),
  autoStartRecording: yup.boolean().notRequired()
})

export const addressValidation = yup.object().shape({
  companyName: validateRequiredString('Billing Company Name'),
  addressLine1: yup.string().nullable(),
  addressLine2: yup.string().nullable(),
  city: yup.string().nullable(),
  state: yup
    .string()
    .nullable()
    .when('country', {
      is: (country) => country?.toLowerCase() === 'india',
      then: (schema) => schema.required('State is required'),
      otherwise: (schema) => schema.nullable()
    }),
  country: validateRequiredString('Country'),
  zipcode: yup.string().nullable()
})

export const billingUpdateSchema = yup.object().shape({
  tenantName: validateRequiredString('Tenant Name'),
  gstinNumber: yup.string().nullable(),
  address: addressValidation
})

// =====================================================================================================
//                                      (o)Job Opening FROM VALIDATIONS
// =====================================================================================================
export const jobOpeningFormValidation = yup.lazy((value) => {
  const isJobOpening = value?.isJobOpening === true

  const base = yup.object().shape({
    isJobOpening: yup.boolean()
  })

  if (isJobOpening) {
    return base.shape({
      title: validateRequiredString('Title'),
      jobDescription: validateRequiredString('Job Description'),
      rolesAndResponsibility: validateRequiredString(
        'Roles and Responsibilities'
      ),
      aboutCompany: validateRequiredString('About Company'),
      keySkills: yup
        .array()
        .of(yup.string().required('Key Skill is required'))
        .min(1, 'At least one key skill is required')
        .required('Key Skills are required'),
      minimumExperience: yup
        .number()
        .transform((value, originalValue) =>
          String(originalValue).trim() === '' ? null : value
        )
        .min(0, 'Minimum Experience must be greater or equal to 0')
        .typeError('Minimum Experience must be a number')
        .required('Minimum Experience is required'),
      maximumExperience: yup
        .number()
        .nullable()
        .transform((value, originalValue) =>
          String(originalValue).trim() === '' ? null : value
        )
        .min(0, 'Maximum Experience must be greater or equal to 0')
        .test(
          'max-greater-than-min',
          'Maximum Experience must be greater than or equal to Minimum Experience',
          function (val) {
            const { minimumExperience } = this.parent
            if (val == null) return true
            if (minimumExperience == null) return true
            return val >= minimumExperience
          }
        ),
      numberOfPositions: yup
        .number()
        .nullable()
        .transform((value, originalValue) =>
          String(originalValue).trim() === '' ? null : value
        )
        .min(0, 'Number of Positions must be greater or equal to 0')
        .typeError('Number of Positions must be a number')
    })
  }

  // otherwise
  return base.shape({
    title: yup.string().nullable(),
    jobDescription: yup.string().nullable(),
    rolesAndResponsibility: yup.string().nullable(),
    aboutCompany: yup.string().nullable(),
    keySkills: yup.array().of(yup.string()).nullable(),
    minimumExperience: yup.string().nullable(),
    maximumExperience: yup.string().nullable(),
    numberOfPositions: yup.string().nullable()
  })
})

export const webhookSchema = yup.object().shape({
  endpoint: yup
    .string()
    .url('Must be a valid URL')
    .required('Webhook endpoint is required'),
  method: yup
    .string()
    .oneOf(['POST', 'GET', 'PUT', 'DELETE', 'PATCH'])
    .required(),
  events: yup.array().min(1, 'Select at least one event'),
  headers: yup.array().of(
    yup.object().shape({
      key: yup.string().required('Key is required'),
      value: yup.string().required('Value is required'),
      needEncryption: yup.boolean()
    })
  )
})

// Coupon Validations

const rateSchema = yup.object().shape({
  discountRate: yup
    .number()
    .transform((value, originalValue) =>
      String(originalValue).trim() === '' ? undefined : value
    )
    .typeError('Discount rate must be a number')
    .min(0, 'Discount must be at least 0')
    .max(100, 'Discount cannot exceed 100')
    .required('Required'),

  inrBasePrice: yup
    .number()
    .transform((value, originalValue) =>
      String(originalValue).trim() === '' ? undefined : value
    )
    .typeError('INR base price must be a number')
    .min(0, 'INR base price cannot be negative')
    .required('Required'),

  inrMaxPrice: yup
    .mixed()
    .test('inr-max', 'INR max must be empty or >= base', function (v) {
      if (v === '' || v === null || v === undefined) return true
      if (isNaN(Number(v)))
        return this.createError({
          message: 'INR max must be a number or empty'
        })
      return Number(v) >= Number(this.parent.inrBasePrice)
    }),

  usdBasePrice: yup
    .number()
    .transform((value, originalValue) =>
      String(originalValue).trim() === '' ? undefined : value
    )
    .typeError('USD base price must be a number')
    .min(0, 'USD base price cannot be negative')
    .required('Required'),

  usdMaxPrice: yup
    .mixed()
    .test('usd-max', 'USD max must be empty or >= base', function (v) {
      if (v === '' || v === null || v === undefined) return true
      if (isNaN(Number(v)))
        return this.createError({
          message: 'USD max must be a number or empty'
        })
      return Number(v) >= Number(this.parent.usdBasePrice)
    })
})

const ratesArraySchema = yup
  .array()
  .of(rateSchema)
  .min(1, 'At least one discount rate is required')
  .test(
    'inr-ordered',
    'INR rate must be sequential and non-overlapping',
    function (arr) {
      if (!arr || arr.length === 0) return true
      for (let i = 0; i < arr.length - 1; i++) {
        const curMax = arr[i].inrMaxPrice || null
        const nextBase = Number(arr[i + 1].inrBasePrice)

        if (curMax === null || curMax === '') {
          return this.createError({
            path: `rates.${i}.inrMaxPrice`,
            message: `INR rate ${
              i + 1
            } must have maxPrice unless it is the last rate.`
          })
        }
        if (!(nextBase > Number(curMax))) {
          return this.createError({
            path: `rates.${i + 1}.inrBasePrice`,
            message: `INR base price in rate ${
              i + 2
            } must be > previous rate's maxPrice`
          })
        }
      }
      return true
    }
  )
  .test(
    'usd-ordered',
    'USD rate must be sequential and non-overlapping',
    function (arr) {
      if (!arr || arr.length === 0) return true
      for (let i = 0; i < arr.length - 1; i++) {
        const curMax = arr[i].usdMaxPrice || null
        const nextBase = Number(arr[i + 1].usdBasePrice)

        if (curMax === null || curMax === '') {
          return this.createError({
            path: `rates.${i}.usdMaxPrice`,
            message: `USD rate ${
              i + 1
            } must have maxPrice unless it is the last rate.`
          })
        }
        if (!(nextBase > Number(curMax))) {
          return this.createError({
            path: `rates.${i + 1}.usdBasePrice`,
            message: `USD base price in rate ${
              i + 2
            } must be > previous rate's maxPrice`
          })
        }
      }
      return true
    }
  )

export const couponSchema = yup.object().shape({
  code: yup.string().required('Code is required'),
  description: yup.string().required('Description is required'),
  activateNow: yup.boolean(),
  startTime: yup.mixed().when('activateNow', {
    is: (val) => val === false,
    then: () => yup.mixed().required('Start time is required'),
    otherwise: () => yup.mixed().notRequired()
  }),
  expirationDate: yup
    .mixed()
    .nullable()
    .test('is-valid-date', 'Enter valid date and time', (value) => {
      if (!value) return true
      return !isNaN(new Date(value).getTime())
    })
    .test(
      'expiration-after-start',
      'Expiration date must be greater than start date',
      function (value) {
        if (!value) return true
        const { startTime } = this.parent
        if (!startTime) return true
        try {
          return new Date(value).getTime() > new Date(startTime).getTime()
        } catch (e) {
          return false
        }
      }
    ),
  isGlobal: yup.boolean(),
  tenantId: yup.mixed().when('isGlobal', {
    is: false,
    then: () => yup.string().required('Tenant is required'),
    otherwise: () => yup.mixed().notRequired()
  }),
  maxDiscountAmountUsd: yup.mixed(),
  maxDiscountAmountInr: yup.mixed(),
  isOneTimeUse: yup.boolean(),
  rates: ratesArraySchema
})
